import React from "react";
import {
  gridPageSelector,
  gridPageSizeSelector,
  gridRowCountSelector,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid-premium";
import { Box, Pagination, PaginationItem, TablePagination } from "@mui/material";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import SkipPreviousIcon from "@mui/icons-material/SkipPrevious";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import { useRowsPerPage } from "../../hooks/useRowsPerPage";

interface DataGridPaginationFooterProps {
  pageSizeOptions?: number[];
  children?: React.ReactNode;
}

const EmptyPaginationActions = () => null;

const paginationItemSlots = {
  first: SkipPreviousIcon,
  previous: ArrowLeftIcon,
  next: ArrowRightIcon,
  last: SkipNextIcon,
};

export const paginationFooterSx = {
  borderTop: "1px solid var(--table-border)",
  backgroundColor: "var(--pagination-bg)",
  color: "var(--table-head)",
  fontFamily: "var(--font-primary)",
  fontSize: "14px",
  fontWeight: 400,
  padding: "10px 4px",
  minHeight: "49px",
  boxSizing: "border-box",
  "& .MuiDataGrid-footerContainer": {
    minHeight: "28px",
    padding: 0,
  },
  "& .MuiPagination-ul": {
    flexWrap: "nowrap",
  },
  "& .MuiPaginationItem-root": {
    minWidth: "28px",
    width: "28px",
    height: "28px",
    margin: "0 1px",
    padding: 0,
    color: "var(--table-head)",
    fontFamily: "var(--font-primary)",
    fontSize: "14px",
    fontWeight: 400,
  },
  "& .MuiTablePagination-root, & .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select": {
    fontFamily: "var(--font-primary)",
    fontSize: "14px",
    fontWeight: 400,
  },
  "& .MuiTablePagination-selectIcon": {
    color: "var(--table-head)",
  },
};

export const selectedPaginationItemSx = {
  // AutoXLR8 keeps page numbers square and renders the first/previous/next/last
  // controls as plain, borderless icons.
  "& .MuiPaginationItem-root:hover": {
    borderRadius: 0,
  },
  "& .MuiPaginationItem-root.Mui-selected": {
    borderRadius: 0,
    backgroundColor: "var(--pagination-selected-bg)",
    color: "var(--text-on-brand)",
    "&:hover": {
      borderRadius: 0,
      backgroundColor: "var(--pagination-selected-bg)",
    },
  },
  "& .MuiPaginationItem-root.MuiPaginationItem-previousNext, & .MuiPaginationItem-root.MuiPaginationItem-firstLast": {
    border: "none",
    backgroundColor: "transparent",
    "&:hover": {
      borderRadius: 0,
      backgroundColor: "transparent",
    },
  },
};

const DataGridPaginationFooter: React.FC<DataGridPaginationFooterProps> = ({
  pageSizeOptions,
  children,
}) => {
  const apiRef = useGridApiContext();
  const page = useGridSelector(apiRef, gridPageSelector);
  const pageSize = useGridSelector(apiRef, gridPageSizeSelector);
  const rowCount = useGridSelector(apiRef, gridRowCountSelector);
  const { rowsPerPageOptions } = useRowsPerPage();
  const pageCount = Math.max(1, Math.ceil((rowCount || 0) / (pageSize || 1)));

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    newPage: number,
  ) => {
    apiRef.current.setPage(newPage - 1);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    apiRef.current.setPageSize(parseInt(event.target.value, 10));
    apiRef.current.setPage(0);
  };

  return (
    <Box
      className="d-flex justify-content-between align-items-center"
      width="100%"
      sx={paginationFooterSx}
    >
      {children}
      <Box
        className="MuiDataGrid-footerContainer"
        sx={{ flexGrow: 1, borderTop: 0 }}
      >
        <Box
          className="d-flex justify-content-between align-items-center"
          width="100%"
        >
          <Pagination
            count={pageCount}
            page={page + 1}
            onChange={handlePageChange}
            showFirstButton
            showLastButton
            renderItem={(item) => (
              <PaginationItem {...item} slots={paginationItemSlots} />
            )}
            sx={selectedPaginationItemSx}
          />
          <TablePagination
            component="div"
            count={rowCount}
            page={page}
            onPageChange={() => {}}
            rowsPerPage={pageSize}
            onRowsPerPageChange={handleRowsPerPageChange}
            ActionsComponent={EmptyPaginationActions}
            rowsPerPageOptions={pageSizeOptions || rowsPerPageOptions}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default DataGridPaginationFooter;
